import React from 'react'
import classNames from 'classnames'
import UserPicture from '@/features/profile/user-picture'
import ButtonIcon from '@/components/ui/ButtonIcon/ButtonIcon'
import { DotsIcon } from '@/assets/icons'
import { useState } from 'react'
import { useRef } from 'react'
import { useEffect } from 'react'
import { createContext, useContext } from 'react'
import PhonePage from '@/features/phone-page'
import FloatingPanel from '@/components/ui/FloatingPanel'
import useIsMobileSize from '@/hooks/useIsMobileSize'

export const DropdownMenuContext = createContext(null)

export const useDropdownMenu = () => {
  const context = useContext(DropdownMenuContext)
  return context
}

const DropdownMenu = props => {
  const [isOpen, setIsOpen] = useState(false)
  const iconRef = useRef(null)
  const listRef = useRef(null)
  const isMobileSize = useIsMobileSize()

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  const handleClose = event => {
    if (!isOpen) {
      return
    }

    const listNode = listRef.current
    if (listNode && listNode.contains(event.target)) {
      return
    }

    const iconNode = iconRef.current
    if (iconNode && !iconNode.contains(event.target)) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClose, false)

    return () => {
      document.removeEventListener('mousedown', handleClose, false)
    }
  }, [isOpen])

  const { renderIcon, children, alwaysKeepHidden, me, itemListClassName } = props
  const isLoggedIn = me && me.id

  const closeMenu = () => {
    setIsOpen(false)
  }

  const open = isOpen && !alwaysKeepHidden

  return (
    <DropdownMenuContext.Provider value={{ closeMenu }}>
      <div
        className={classNames('dropdown-menu relative', {
          'dropdown-menu--open': open,
          'dropdown-menu--loggedIn': me,
        })}>
        {isLoggedIn ? (
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            className={classNames(
              'dropdown-menu__icon flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-transparent text-[var(--color-Neutral-5)] transition-all duration-200 bg-transparent p-0 outline-offset-2 focus-visible:outline-[var(--color-brand-04)]',
              'noBorder box-content [&_.user-picture]:!m-0',
              open &&
                'border-[var(--color-primary-pure)] bg-[var(--color-surface-2)] text-[var(--color-primary-pure)]',
            )}
            onClick={handleToggle}
            ref={iconRef}>
            <UserPicture
              user={me}
              width={48}
              height={48}
              className="overflow-hidden rounded-[var(--radius-circular)] border-none"
              imageClassName="rounded-[var(--radius-circular)]"
            />
          </button>
        ) : (
          <div
            ref={iconRef}
            role="button"
            tabIndex={0}
            className="flex cursor-pointer items-center"
            onClick={handleToggle}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleToggle()
              }
            }}>
            {typeof renderIcon === 'function' ? (
              renderIcon()
            ) : (
              <ButtonIcon size="medium" highlightOnHover cursorPointer isFocused={open}>
                <DotsIcon />
              </ButtonIcon>
            )}
          </div>
        )}
        {isMobileSize ? (
          // Mobile: PhonePage renders the bottom-sheet / full-page itself
          open && <PhonePage onClose={closeMenu}>{children}</PhonePage>
        ) : (
          // Desktop: anchored FloatingPanel
          <FloatingPanel
            isOpen={open}
            ref={listRef}
            className={classNames(
              'right-0 z-[1000] mt-2.5 max-h-[calc(100vh-248px)] overflow-y-auto py-2',
              itemListClassName,
            )}>
            {children}
          </FloatingPanel>
        )}
      </div>
    </DropdownMenuContext.Provider>
  )
}

export default DropdownMenu
